import React, { useEffect, useState } from "react"
import firebase from "firebase"

import { db } from "../../../utils/firebase"
import Table from "../../../components/structure/table/table"
import { LoadingIcon } from "../../../utils/svgs"

import QueueWebsiteRow from "./queue-website-row"

/**
 * Read-only view over the latest 100 website enquiries. Source data is the
 * `enquiries` collection (the live customer-facing one); each enquiry's
 * client is hydrated via a batched lookup against `clients` so rows can
 * display name/email/phone without N+1 reads.
 *
 * Parsing here is transient — the modal runs the parser against an inline
 * payload assembled from the enquiry + client, and shows the result in the
 * modal only. Nothing is written back.
 */
function EnquiryQueueWebsiteTab() {
	const [enquiries, setEnquiries] = useState([])
	const [clientsById, setClientsById] = useState({})
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		const unsubscribe = db
			.collection("enquiries")
			.orderBy("created", "desc")
			.limit(100)
			.onSnapshot(
				(snapshot) => {
					setEnquiries(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
					setLoading(false)
				},
				(err) => {
					console.error("Failed to subscribe to enquiries", err)
					setLoading(false)
				}
			)
		return () => unsubscribe()
	}, [])

	// Hydrate client docs for any enquiries we've loaded but not yet looked
	// up. Firestore `in` queries cap at 10 ids per query, so we batch.
	useEffect(() => {
		const missing = Array.from(
			new Set(
				enquiries
					.map((enquiry) => enquiry.client)
					.filter((id) => id && !clientsById[id])
			)
		)
		if (missing.length === 0) return

		let cancelled = false

		const load = async () => {
			const next = {}
			for (let i = 0; i < missing.length; i += 10) {
				const batch = missing.slice(i, i + 10)
				try {
					const snap = await db
						.collection("clients")
						.where(firebase.firestore.FieldPath.documentId(), "in", batch)
						.get()
					snap.forEach((doc) => {
						next[doc.id] = doc.data()
					})
				} catch (err) {
					console.error("Failed to batch-load clients", err)
				}
			}
			if (cancelled) return
			setClientsById((prev) => ({ ...prev, ...next }))
		}

		load()
		return () => {
			cancelled = true
		}
	}, [enquiries, clientsById])

	return (
		<Table
			className="mailboxes-table"
			headings={[
				"Added to queue",
				"Parsed",
				"Client name",
				"Client email",
				"Client phone",
				"Status",
				"Fields completed",
				"",
			]}
			noResults={!loading && enquiries.length === 0}
			noResultsMessage="No website enquiries found.">
			{enquiries.map((enquiry) => (
				<QueueWebsiteRow
					key={enquiry.id}
					enquiry={enquiry}
					client={enquiry.client ? clientsById[enquiry.client] : null}
				/>
			))}

			{loading && (
				<div className="table-loading-splash">
					<div className="loading-wrapper">
						<p>Loading...</p>
						<div className="svg-loading-wrap">
							<LoadingIcon />
						</div>
					</div>
				</div>
			)}
		</Table>
	)
}

export default EnquiryQueueWebsiteTab
